import React from 'react';
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    unternehmen: [
      { label: 'Über uns', id: 'about' },
      { label: 'Leistungen', id: 'services' },
      { label: 'Projekte', id: 'projects' },
      { label: 'Kontakt', id: 'contact' }
    ],
    leistungen: [
      { label: 'Sanierung', id: 'services', key: 'sanierung' },
      { label: 'Schlüsselfertiger Bau', id: 'services', key: 'schluesselfertiger-bau' },
      { label: 'Innenausbau', id: 'services', key: 'innenausbau' },
      { label: 'Planung', id: 'services', key: 'planung' }
    ]
  };

  return (
    <footer className="relative overflow-hidden">
      {/* Diagonal Background Design */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#003366] via-[#004d99] to-[#00475d]"></div>
        {/* Diagonal accent */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-[#00CCDD]/20 to-transparent transform skew-x-12"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Company Info */}
          <div className="space-y-6">
            {/* Original Logo Image - inverted for dark background */}
            <div className="cursor-pointer group" onClick={scrollToTop}>
              <img 
                src="https://customer-assets.emergentagent.com/job_dde5972f-5219-42ab-9192-5714e8a2e750/artifacts/zwvvm7v5_Black_and_White_Minimalist_Wedding_Monogram_Logo.pdf.png"
                alt="THS Bauprojekte GmbH Logo"
                className="h-20 w-auto object-contain brightness-0 invert group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            
            <p className="text-white/80 leading-relaxed">
              Ihr zuverlässiger Partner für hochwertige Bau- und Sanierungsprojekte in Bonn und Umgebung.
            </p>
            
            {/* Social Media */}
            <div className="flex gap-3">
              {[
                { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/ths.bauprojekte' },
                { icon: Facebook, label: 'Facebook', href: '#' },
                { icon: Linkedin, label: 'LinkedIn', href: '#' }
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target={social.icon === Instagram ? '_blank' : undefined}
                  rel={social.icon === Instagram ? 'noopener noreferrer' : undefined}
                  aria-label={social.label}
                  className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-[#00CCDD] transition-all duration-300 group"
                >
                  <social.icon className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                </a>
              ))}
            </div>
          </div>

          {/* Unternehmen Links */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6 relative inline-block">
              Unternehmen
              <div className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-[#00CCDD] to-transparent"></div>
            </h4>
            <ul className="space-y-3">
              {footerLinks.unternehmen.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-white/70 hover:text-[#00CCDD] transition-colors duration-200 flex items-center group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-[#00CCDD] mr-0 group-hover:mr-2 transition-all duration-200"></span>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Leistungen Links */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6 relative inline-block">
              Leistungen
              <div className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-[#00CCDD] to-transparent"></div>
            </h4>
            <ul className="space-y-3">
              {footerLinks.leistungen.map((link) => (
                <li key={link.key}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-white/70 hover:text-[#00CCDD] transition-colors duration-200 flex items-center group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-[#00CCDD] mr-0 group-hover:mr-2 transition-all duration-200"></span>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6 relative inline-block">
              Kontakt
              <div className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-[#00CCDD] to-transparent"></div>
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 group">
                <MapPin className="w-5 h-5 text-[#00CCDD] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                <span className="text-white/70 text-sm">{companyInfo.address}</span>
              </li>
              <li className="flex items-center gap-3 group">
                <Phone className="w-5 h-5 text-[#00CCDD] flex-shrink-0 group-hover:scale-110 transition-transform" />
                <a
                  href={`tel:${companyInfo.phone.replace(/\\s/g, '')}`}
                  className="text-white/70 hover:text-[#00CCDD] transition-colors text-sm"
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3 group">
                <Mail className="w-5 h-5 text-[#00CCDD] flex-shrink-0 group-hover:scale-110 transition-transform" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-white/70 hover:text-[#00CCDD] transition-colors text-sm"
                >
                  {companyInfo.email}
                </a>
              </li>
            </ul>
            
            {/* Opening Hours Badge */}
            <div className="mt-6 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
              <p className="text-white font-semibold text-sm mb-1">Öffnungszeiten</p>
              <p className="text-white/70 text-sm">{companyInfo.openingHours}</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar with Diagonal Separator */}
        <div className="relative pt-8">
          {/* Diagonal separator line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8">
            <p className="text-white/60 text-sm text-center md:text-left">
              © {currentYear} THS Bauprojekte GmbH. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/impressum" className="text-white/60 hover:text-[#00CCDD] text-sm transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-white/60 hover:text-[#00CCDD] text-sm transition-colors">
                Datenschutz
              </Link>
              <Link to="/agb" className="text-white/60 hover:text-[#00CCDD] text-sm transition-colors">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-14 h-14 rounded-full bg-gradient-to-r from-[#004d99] to-[#00CCDD] text-white shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center group"
        aria-label="Nach oben scrollen"
      >
        <ArrowUp className="w-6 h-6 group-hover:-translate-y-1 transition-transform" />
      </button>
    </footer>
  );
};